import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { UnifiedAuthProvider, useUnifiedAuth } from './hooks/useUnifiedAuth';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import LanguageSwitcher from './components/LanguageSwitcher';
import AuthModal from './components/AuthModal';
import { initializeTokenSecurity } from './utils/tokenSecurity';
import { generateBookingReceiptPdf } from './utils/bookingReceiptPdf';
import { shareContent, onDeepLink } from './utils/nativeBridge';
import EnhancedAuthModal from './components/EnhancedAuthModal';
import { trackPageView, setUserProperties } from './utils/analytics';
import SearchComponent from './components/SearchComponent';
import SearchPage from './components/search/SearchPage';
import HealthcareProvidersPage from './components/HealthcareProvidersPage';
import DoctorsPage from './components/DoctorsPage';
import BookingSystem from './components/booking/BookingSystem';
import ProviderBookingFlow from './components/ProviderBookingFlow';
import DoctorBookingFlow from './components/DoctorBookingFlow';
import PaymentPage from './components/PaymentPage';
import ConsultationHub from './components/consultations/ConsultationHub';
import RevenueDashboard from './components/dashboard/RevenueDashboard';
import PatientProfile from './components/profiles/PatientProfile';
import AuthenticatedHome from './components/AuthenticatedHome';
import MedicalRecords from './components/MedicalRecords';
import Calendar from './components/Calendar';
import AdminPanel from './components/AdminPanel';
import AdminSubscriptionsEnhanced from './components/AdminSubscriptionsEnhanced';
import ClinicDashboard from './components/ClinicDashboard';
import SMSDemo from './components/SMSDemo';
import Notifications from './components/Notifications';
import Messages from './components/Messages';
import NotificationBell from './components/NotificationBell';
import Footer from './components/Footer';
import PageTracker from './components/PageTracker';
import AboutUs from './components/AboutUs';
import GlobalSearchComponent from './components/GlobalSearchComponent';
import GuestBooking from './components/GuestBooking';
import AdminDashboard from './components/AdminDashboard';
import PACIShowcase from './components/PACIShowcase';
import WalletDashboard from './components/WalletDashboard';
import EnhancedSignupDemo from './components/EnhancedSignupDemo';
import RegisterPage from './components/RegisterPage';
import ProviderSignupPage from './components/ProviderSignupPage';
import DoctorSignupPage from './components/DoctorSignupPage';
import ProviderPricingPage from './components/ProviderPricingPage';
import BookingManagePage from './components/BookingManagePage';
import InstallPWA from './components/InstallPWA';
import StaffDailySchedule from './components/StaffDailySchedule';
import StaffTimelineBoard from './components/StaffTimelineBoard';
import WalkInEarnings from './components/WalkInEarnings';
import StaffEarnings from './components/StaffEarnings';
import StaffScheduleLinkView from './components/StaffScheduleLinkView';
import AddToCalendar from './components/AddToCalendar';
import MyClients from './components/MyClients';
import ClientProfile from './components/ClientProfile';
import AIAssistant from './components/AIAssistant';
import ReviewSection from './components/ReviewSection';
import SiteHeader from './components/site/SiteHeader';
import LandingPage from './components/site/LandingPage';
import { UserTerms } from './components/TermsAndConditions';

// Lazy load heavy dashboard components that use FullCalendar
const ProviderDashboard = React.lazy(() => import('./components/ProviderDashboard'));
const SimpleProviderDashboard = React.lazy(() => import('./components/SimpleProviderDashboard'));
import ProviderDashboardTest from './components/ProviderDashboardTest';
// New improved provider dashboard
import ImprovedProviderDashboard from './components/ImprovedProviderDashboard';
import SimpleCalendlyStyleDashboard from './components/SimpleCalendlyStyleDashboard';
import SuperSimpleProviderDashboard from './components/SuperSimpleProviderDashboard';
import SimpleDoctorDashboard from './components/SimpleDoctorDashboard';
import DoctorAppointments from './components/DoctorAppointments';
import DoctorProfileBuilder from './components/DoctorProfileBuilder';
import DoctorAppointmentsNew from './components/DoctorAppointmentsNew';
import ProfilePage from './components/ProfilePage';
import SubscriptionManagement from './components/SubscriptionManagement';
import InfluencerDashboard from './components/InfluencerDashboard';
import './App.css';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';

// Import services
import searchService from './services/searchService';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple Enhanced Logout Button Component
const LogoutButton = ({ user, userRole, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const handleSimpleLogout = () => {
    // Log the logout action
    console.log('User logout initiated:', {
      user_id: user.id,
      user_type: userRole,
      timestamp: new Date().toISOString()
    });

    // Clear role-specific data
    if (userRole === 'doctor') {
      localStorage.removeItem('doctor_availability');
      localStorage.removeItem('clinic_settings');
    } else if (userRole === 'provider') {
      localStorage.removeItem('provider_dashboard');
      localStorage.removeItem('admin_settings');
    } else {
      localStorage.removeItem('patient_appointments');
      localStorage.removeItem('favorite_doctors');
    }

    onLogout();
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 text-sm font-medium shadow-md hover:shadow-lg"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
        </svg>
        <span>{t('logout')}</span>
        <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className={`absolute ${isRTL ? 'left-0' : 'right-0'} mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-50`}>
            <div className="p-4 border-b border-gray-200">
              <div className={`flex items-center gap-3`}>
                {user.picture && (
                  <img src={user.picture} alt="Profile" className="w-8 h-8 rounded-full" />
                )}
                <div className={isRTL ? 'text-right' : 'text-left'}>
                  <p className="font-medium text-gray-900 text-sm">{user.name}</p>
                  <p className="text-xs text-gray-500 capitalize">
                    {userRole === 'doctor' ? '👨‍⚕️ Doctor' : userRole === 'provider' ? '🏥 Provider' : '👤 User'}
                  </p>
                </div>
              </div>
            </div>
            <div className="py-2">
              <button
                onClick={handleSimpleLogout}
                className={`w-full px-4 py-3 ${isRTL ? 'text-right' : 'text-left'} hover:bg-gray-50 flex items-center gap-3 text-gray-700 hover:text-gray-900`}
              >
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <div>
                  <p className="font-medium text-sm">{t('logout')}</p>
                  <p className="text-xs text-gray-500">Sign out securely</p>
                </div>
              </button>
            </div>
            <div className="p-3 bg-gray-50 border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center">
                Logout clears cached medical data
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Header Component



// Enhanced Search Page (Legacy - to be replaced)
const LegacySearchPage = () => {
  const [searchResults, setSearchResults] = useState([]);
  const [searchType, setSearchType] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();

  const handleSearchResults = (results, type, errorMsg = '') => {
    console.log('Search results received:', { results, type, resultCount: results?.length || 0, errorMsg });
    setSearchResults(results);
    setSearchType(type);
    setError(errorMsg);
    setLoading(false);
  };

  const ResultCard = ({ item, type }) => {
    if (type === 'doctors') {
      return (
        <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer border border-gray-100"
             onClick={() => navigate(`/doctors/${item.id}`)}>
          <div className={`flex items-start`}>
            <img
              src={item.profile_image}
              alt={item.name}
              className={`w-16 h-16 rounded-full object-cover shadow-md ${isRTL ? 'ml-4' : 'mr-4'}`}
            />
            <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              <h3 className="text-lg font-bold text-gray-900 mb-1">{item.name}</h3>
              <p className="text-blue-600 font-semibold mb-1">{item.speciality}</p>
              <p className="text-gray-600 text-sm mb-2">{item.clinic_name}</p>
              <div className={"flex items-center gap-4 justify-start"}>
                <div className={`flex items-center`}>
                  <span className="text-yellow-400 text-sm">★</span>
                  <span className={`text-gray-700 text-sm font-medium ${isRTL ? 'mr-1' : 'ml-1'}`}>{item.rating}</span>
                </div>
                <span className="text-green-600 font-semibold text-sm">${item.consultation_fee}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (type === 'clinics') {
      return (
        <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100">
          <div className={`flex items-start`}>
            {item.logo && (
              <img
                src={item.logo}
                alt={item.name}
                className={`w-16 h-16 rounded-lg object-cover shadow-md ${isRTL ? 'ml-4' : 'mr-4'}`}
              />
            )}
            <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              <h3 className="text-lg font-bold text-gray-900 mb-1">{item.name}</h3>
              <p className="text-gray-600 mb-2">{item.address}</p>
              <div className={`flex flex-wrap gap-2 mb-2 ${isRTL ? 'justify-end' : 'justify-start'}`}>
                {item.specialities?.map((spec, index) => (
                  <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                    {spec}
                  </span>
                ))}
              </div>
              <p className="text-gray-500 text-sm">
                {item.total_doctors || item.doctor_count} {t('doctors').toLowerCase()}
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (type === 'providers') {
      return (
        <div className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer border border-gray-100">
          <div className={`flex items-start`}>
            {item.logo && (
              <img
                src={item.logo}
                alt={item.name}
                className={`w-16 h-16 rounded-lg object-cover shadow-md ${isRTL ? 'ml-4' : 'mr-4'}`}
              />
            )}
            <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              <h3 className="text-lg font-bold text-gray-900 mb-1">{item.name}</h3>
              <p className="text-gray-600 text-sm mb-2">{item.address}</p>
              <div className={"flex items-center gap-2 mb-2 justify-start"}>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  item.facility_type === 'hospital' ? 'bg-red-100 text-red-800' :
                  item.facility_type === 'medical_center' ? 'bg-blue-100 text-blue-800' :
                  item.facility_type === 'laboratory' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {item.facility_type === 'medical_center' ? '🏥 Medical Center' :
                   item.facility_type === 'hospital' ? '🏨 Hospital' :
                   item.facility_type === 'laboratory' ? '🧪 Laboratory' :
                   item.facility_type === 'pharmacy' ? '💊 Pharmacy' :
                   `🏢 ${item.facility_type}`}
                </span>
                <div className={`flex items-center`}>
                  <span className="text-yellow-400 text-sm">★</span>
                  <span className={`text-gray-700 text-sm font-medium ${isRTL ? 'mr-1' : 'ml-1'}`}>
                    {item.average_rating || 0.0}
                  </span>
                  <span className="text-gray-500 text-xs ml-1">
                    ({item.total_reviews || 0} reviews)
                  </span>
                </div>
              </div>
              <div className={`flex flex-wrap gap-1 mb-2 ${isRTL ? 'justify-end' : 'justify-start'}`}>
                {item.services?.slice(0, 3).map((service, index) => (
                  <span key={index} className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                    {typeof service === 'string' ? service : service.name || service}
                  </span>
                ))}
                {item.services?.length > 3 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">
                    +{item.services.length - 3} more
                  </span>
                )}
              </div>
              <p className="text-gray-500 text-sm">
                {item.total_doctors || 0} {t('doctors').toLowerCase()} • {item.specialities?.length || 0} specialities
              </p>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
        <h3 className="text-lg font-bold text-gray-900 mb-2">{item.name}</h3>
        <p className="text-gray-600">{item.email}</p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white relative overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-72 h-72 bg-white rounded-full mix-blend-multiply filter blur-xl transform translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-300 rounded-full mix-blend-multiply filter blur-xl transform -translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className={`text-center ${isRTL ? 'text-right' : 'text-left'} lg:text-center`}>
            <h1 className="text-4xl lg:text-5xl font-bold mb-4">
              {t('searchHealthcareProviders')}
            </h1>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              {t('findDoctorsClinicsServices')}
            </p>
          </div>
        </div>
        
        {/* Wave Bottom */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" className="w-full h-6 sm:h-8">
            <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" fill="rgb(249 250 251)"/>
          </svg>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <SearchComponent 
              onResults={handleSearchResults} 
              placeholder={t('searchDoctorsClinics')}
            />
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8 shadow-sm">
              <div className={`flex items-center`}>
                <svg className={`w-5 h-5 text-red-600 ${isRTL ? 'ml-3' : 'mr-3'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-700 font-medium">{error}</p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="text-center py-16">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 text-lg">{t('searching')}</p>
            </div>
          )}

          {/* Results Display */}
          {!loading && searchResults && (
            <div className="space-y-8">
              {searchType === 'all' ? (
                <>
                  {searchResults.doctors?.length > 0 && (
                    <div>
                      <div className={`flex items-center mb-6`}>
                        <div className="bg-gradient-to-r from-blue-600 to-blue-700 w-12 h-12 rounded-xl flex items-center justify-center shadow-lg">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <div className={isRTL ? 'mr-4' : 'ml-4'}>
                          <h2 className="text-2xl font-bold text-gray-900">
                            {t('doctorsResults')} ({searchResults.doctors.length})
                          </h2>
                          <p className="text-gray-600">{searchResults.doctors.length} {t('resultsFound')}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                        {searchResults.doctors.map((doctor) => (
                          <ResultCard key={doctor.id} item={doctor} type="doctors" />
                        ))}
                      </div>
                    </div>
                  )}

                  {searchResults.clinics?.length > 0 && (
                    <div>
                      <div className={`flex items-center mb-6`}>
                        <div className="bg-gradient-to-r from-green-600 to-green-700 w-12 h-12 rounded-xl flex items-center justify-center shadow-lg">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <div className={isRTL ? 'mr-4' : 'ml-4'}>
                          <h2 className="text-2xl font-bold text-gray-900">
                            {t('clinicsResults')} ({searchResults.clinics.length})
                          </h2>
                          <p className="text-gray-600">{searchResults.clinics.length} {t('resultsFound')}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                        {searchResults.clinics.map((clinic) => (
                          <ResultCard key={clinic.id} item={clinic} type="clinics" />
                        ))}
                      </div>
                    </div>
                  )}

                  {searchResults.providers?.length > 0 && (
                    <div>
                      <div className={`flex items-center mb-6`}>
                        <div className="bg-gradient-to-r from-purple-600 to-purple-700 w-12 h-12 rounded-xl flex items-center justify-center shadow-lg">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                        </div>
                        <div className={isRTL ? 'mr-4' : 'ml-4'}>
                          <h2 className="text-2xl font-bold text-gray-900">
                            Providers ({searchResults.providers.length})
                          </h2>
                          <p className="text-gray-600">{searchResults.providers.length} {t('resultsFound')}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {searchResults.providers.map((provider) => (
                          <ResultCard key={provider.id} item={provider} type="providers" />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div>
                  <div className={`flex items-center mb-6`}>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-lg ${
                      searchType === 'doctors' ? 'bg-gradient-to-r from-blue-600 to-blue-700' : 
                      searchType === 'providers' ? 'bg-gradient-to-r from-purple-600 to-purple-700' :
                      'bg-gradient-to-r from-green-600 to-green-700'
                    }`}>
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {searchType === 'doctors' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                        )}
                      </svg>
                    </div>
                    <div className={isRTL ? 'mr-4' : 'ml-4'}>
                      <h2 className="text-2xl font-bold text-gray-900">
                        {searchType === 'doctors' ? t('doctorsResults') : 
                         searchType === 'providers' ? 'Providers' :
                         t('clinicsResults')} ({searchResults.length})
                      </h2>
                      <p className="text-gray-600">{searchResults.length} {t('resultsFound')}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {searchResults.map((item) => (
                      <ResultCard key={item.id} item={item} type={searchType} />
                    ))}
                  </div>
                </div>
              )}

              {/* No Results State */}
              {((searchType === 'all' && (!searchResults.doctors?.length && !searchResults.clinics?.length && !searchResults.providers?.length)) ||
                (searchType !== 'all' && searchResults.length === 0)) && (
                <div className="text-center py-16 bg-white rounded-xl shadow-lg">
                  <div className="text-6xl mb-6">🔍</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('noResults')}</h3>
                  <p className="text-gray-600 text-lg max-w-md mx-auto leading-relaxed">
                    {t('adjustSearch')}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const { handleGoogleCallback } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check both hash and query parameters for session_id
    const urlParams = new URLSearchParams(location.search);
    const hashParams = new URLSearchParams(location.hash.substring(1));
    
    const sessionId = urlParams.get('session_id') || hashParams.get('session_id');
    
    if (sessionId) {
      handleAuthCallback(sessionId);
    } else {
      navigate('/');
    }
  }, [location]);

  const handleAuthCallback = async (sessionId) => {
    try {
      console.log('Processing authentication callback with session ID:', sessionId?.substring(0, 8) + '...');
      const result = await handleGoogleCallback(sessionId);
      if (result.success) {
        console.log('Authentication successful');
        navigate('/');
      } else {
        console.error('Authentication failed:', result.error);
        // Show user-friendly error message
        alert('Authentication failed: ' + (result.error || 'Please try again later.'));
        navigate('/');
      }
    } catch (error) {
      console.error('Authentication error:', error);
      alert('Authentication failed. Please try again later.');
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
        <p className="text-gray-600">Completing authentication...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user: legacyUser, loading: legacyLoading } = useAuth();
  const { user: unifiedUser, loading: unifiedLoading, isAuthenticated } = useUnifiedAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const navigate = useNavigate();
  
  // Use unified auth if available, fallback to legacy
  const user = unifiedUser || legacyUser;
  const loading = unifiedLoading || legacyLoading;
  const authenticated = isAuthenticated || !!legacyUser;
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
        <p className="text-gray-600 mt-4">Loading...</p>
      </div>
    );
  }
  
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center bg-white rounded-lg shadow-lg p-8">
          <div className="mb-6">
            <div className="mx-auto h-12 w-12 text-red-500 mb-4">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                      d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Required</h2>
            <p className="text-gray-600 mb-6">Please sign in to access this feature.</p>
          </div>
          
          <button
            onClick={() => setShowAuthModal(true)}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Sign In
          </button>
          
          {showAuthModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Sign In Required</h3>
                  <button 
                    onClick={() => setShowAuthModal(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>
                <AuthModal onClose={() => setShowAuthModal(false)} />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
  
  // Check if admin-only route
  if (adminOnly) {
    const isAdmin = user?.is_admin || user?.user_type === 'admin';
    
    if (!isAdmin) {
      // Not an admin - redirect to home or show error
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="max-w-md mx-auto text-center bg-white rounded-lg shadow-lg p-8">
            <div className="text-red-600 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-6">This page is only accessible to administrators.</p>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Home
            </button>
          </div>
        </div>
      );
    }
  }
  
  return children;
};

// Placeholder components (using original implementation temporarily)
const DoctorList = () => {
  const [doctors, setDoctors] = useState([]);
  const [specialities, setSpecialities] = useState([]);
  const [selectedSpeciality, setSelectedSpeciality] = useState('');
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();

  useEffect(() => {
    fetchDoctors();
    fetchSpecialities();
  }, [selectedSpeciality]);

  const fetchDoctors = async () => {
    try {
      setLoading(true);
      const params = selectedSpeciality ? { speciality: selectedSpeciality } : {};
      const response = await axios.get(`${API}/doctors`, { params });
      setDoctors(response.data);
    } catch (error) {
      console.error('Error fetching doctors:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSpecialities = async () => {
    try {
      const response = await axios.get(`${API}/specialities`);
      setSpecialities(response.data);
    } catch (error) {
      console.error('Error fetching specialities:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className={`text-3xl font-bold text-gray-900 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
            {t('findDoctor')}
          </h1>
          
          <div className="bg-white p-4 rounded-lg shadow">
            <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
              {t('filterBySpeciality')}
            </label>
            <select
              value={selectedSpeciality}
              onChange={(e) => setSelectedSpeciality(e.target.value)}
              className={`w-full md:w-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isRTL ? 'text-right' : 'text-left'}`}
            >
              <option value="">{t('allSpecialities')}</option>
              {specialities.map((speciality) => (
                <option key={speciality} value={speciality}>
                  {speciality}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">{t('loadingDoctors')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {doctors.map((doctor) => (
              <div key={doctor.id} className="bg-white rounded-lg shadow-lg p-6 hover:shadow-xl transition-shadow">
                <div className="text-center mb-4">
                  <img
                    src={doctor.profile_image}
                    alt={doctor.name}
                    className="w-24 h-24 rounded-full mx-auto mb-4 object-cover"
                  />
                  <h3 className="text-xl font-semibold text-gray-900">{doctor.name}</h3>
                  <p className="text-blue-600 font-medium">{doctor.speciality}</p>
                  <p className="text-gray-600">{doctor.qualification}</p>
                </div>
                
                <div className={`space-y-2 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
                  <div className={`flex items-center justify-between`}>
                    <span className="text-gray-600">{t('experience')}:</span>
                    <span className="font-medium">{doctor.experience_years} {t('years')}</span>
                  </div>
                  <div className={`flex items-center justify-between`}>
                    <span className="text-gray-600">{t('consultationFee')}:</span>
                    <span className="font-medium text-green-600">${doctor.consultation_fee}</span>
                  </div>
                  <div className={`flex items-center justify-between`}>
                    <span className="text-gray-600">{t('rating')}:</span>
                    <span className={`flex items-center`}>
                      <span className="text-yellow-400">★</span>
                      <span className={isRTL ? 'mr-1' : 'ml-1'}>{doctor.rating} ({doctor.total_reviews})</span>
                    </span>
                  </div>
                </div>
                
                <button
                  onClick={() => navigate(`/doctors/${doctor.id}`)}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('viewProfileBook')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const DoctorDetail = () => {
  const { id } = useParams();
  const location = useLocation();
  const [doctor, setDoctor] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [symptoms, setSymptoms] = useState('');
  const [loading, setLoading] = useState(true);
  const [availableSlots, setAvailableSlots] = useState({});
  const [bookedSlots, setBookedSlots] = useState({});
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();
  
  // Get reschedule info from location state
  const { rescheduleMode, appointmentId, currentDate, currentTime } = location.state || {};
  const isRescheduling = rescheduleMode && appointmentId;

  useEffect(() => {
    fetchDoctor();
    fetchAvailability();
  }, [id]);

  const fetchDoctor = async () => {
    try {
      const response = await axios.get(`${API}/doctors/${id}`);
      setDoctor(response.data);
    } catch (error) {
      console.error('Error fetching doctor:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailability = async () => {
    try {
      // Fetch doctor's availability and booked slots
      const availabilityResponse = await axios.get(`${API}/doctors/${id}/availability`);
      setAvailableSlots(availabilityResponse.data);
      
      const bookedResponse = await axios.get(`${API}/doctors/${id}/booked-slots`);
      setBookedSlots(bookedResponse.data);
    } catch (error) {
      console.error('Error fetching availability:', error);
    }
  };

  const handleBookAppointment = async () => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (!selectedDate || !selectedTime) {
      alert('Please select date and time');
      return;
    }

    try {
      const token = localStorage.getItem('sessionToken');
      
      if (isRescheduling) {
        // Reschedule existing appointment
        await axios.put(
          `${API}/appointments/${appointmentId}/reschedule?new_date=${selectedDate}&new_time=${selectedTime}`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        alert('Appointment rescheduled successfully!');
      } else {
        // Create new appointment
        const response = await axios.post(`${API}/appointments`, {
          doctor_id: doctor.id,
          appointment_date: selectedDate,
          appointment_time: selectedTime,
          symptoms: symptoms,
          payment_method: 'myfatoorah'
        }, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        const appointmentData = response.data;
        
        // Check if payment is required
        if (appointmentData.requires_payment && appointmentData.payment_url) {
          // Redirect to MyFatoorah payment page
          window.location.href = appointmentData.payment_url;
          return;
        }
        
        alert('Appointment booked successfully!');
      }
      
      navigate('/appointments');
    } catch (error) {
      console.error('Error booking appointment:', error);
      console.error('Error details:', error.response?.data);
      const errorMsg = error.response?.data?.detail || 'Failed to book appointment. Please try again.';
      alert(errorMsg);
    }
  };

  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 14; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    
    return dates;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">{t('doctorNotFound')}</h2>
          <button
            onClick={() => navigate('/doctors')}
            className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            {t('backToDoctors')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {isRescheduling && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h2 className="text-lg font-semibold text-blue-900">
              🔄 Rescheduling Appointment
            </h2>
            <p className="text-sm text-blue-700 mt-1">
              Current: {currentDate} at {currentTime}
            </p>
            <p className="text-sm text-blue-600">Select a new date and time below</p>
          </div>
        )}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <div className={`text-center mb-6 ${isRTL ? 'text-right' : 'text-left'} md:text-center`}>
                <img
                  src={doctor.profile_image}
                  alt={doctor.name}
                  className="w-32 h-32 rounded-full mx-auto mb-4 object-cover"
                />
                <h1 className="text-3xl font-bold text-gray-900">{doctor.name}</h1>
                <p className="text-xl text-blue-600 font-medium">{doctor.speciality}</p>
                <p className="text-gray-600">{doctor.qualification}</p>
              </div>
              
              <div className="space-y-4">
                <div className={`flex items-center justify-between py-2 border-b`}>
                  <span className="text-gray-600">{t('experience')}:</span>
                  <span className="font-medium">{doctor.experience_years} {t('years')}</span>
                </div>
                <div className={`flex items-center justify-between py-2 border-b`}>
                  <span className="text-gray-600">{t('consultationFee')}:</span>
                  <span className="font-medium text-green-600">${doctor.consultation_fee}</span>
                </div>
                <div className={`flex items-center justify-between py-2 border-b`}>
                  <span className="text-gray-600">{t('rating')}:</span>
                  <span className={`flex items-center`}>
                    <span className="text-yellow-400">★</span>
                    <span className={isRTL ? 'mr-1' : 'ml-1'}>{doctor.rating} ({doctor.total_reviews} {t('reviews')})</span>
                  </span>
                </div>
                <div className="py-2">
                  <span className={`text-gray-600 block mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>{t('clinic')}:</span>
                  <div className={"flex items-center gap-3"}>
                    {doctor.clinic_logo && (
                      <img
                        src={doctor.clinic_logo}
                        alt="Clinic Logo"
                        className="w-12 h-12 object-cover rounded-lg"
                      />
                    )}
                    <div>
                      <p className="font-medium">{doctor.clinic_name}</p>
                      <p className="text-gray-600">{doctor.clinic_address}</p>
                    </div>
                  </div>
                </div>
                <div className="py-2">
                  <span className="text-gray-600 block mb-2">About:</span>
                  <p className="text-gray-700">{doctor.about}</p>
                </div>
              </div>
            </div>

            <div>
              <h2 className={`text-2xl font-bold text-gray-900 mb-6 ${isRTL ? 'text-right' : 'text-left'}`}>
                {t('bookAppointment')}
              </h2>
              
              <div className="space-y-6">
                {/* Calendar Component */}
                <div>
                  <Calendar 
                    selectedDate={selectedDate}
                    onDateSelect={setSelectedDate}
                    availableSlots={availableSlots}
                    bookedSlots={bookedSlots}
                    mode="patient"
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('symptomsReason')}
                  </label>
                  <textarea
                    value={symptoms}
                    onChange={(e) => setSymptoms(e.target.value)}
                    rows={3}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${isRTL ? 'text-right' : 'text-left'}`}
                    placeholder={t('describeSymptomsPlaceholder')}
                  />
                </div>

                <button
                  onClick={handleBookAppointment}
                  disabled={!selectedDate}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {t('bookAppointmentFee', { fee: doctor.consultation_fee })}
                </button>

                {!user && (
                  <p className={`text-sm text-gray-600 text-center ${isRTL ? 'text-right' : 'text-left'}`}>
                    {t('signInToBook')}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <ReviewSection providerId={id} providerType="doctor" />
      </div>
    </div>
  );
};

const ProviderDetail = () => {
  const { id } = useParams();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [facilityImages, setFacilityImages] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProvider();
  }, [id]);

  const fetchProvider = async () => {
    try {
      const response = await axios.get(`${API}/healthcare-providers/${id}`);
      setProvider(response.data);
      // Fetch facility images
      if (response.data?.user_id) {
        try {
          const imgRes = await axios.get(`${API}/profile/pictures/${response.data.user_id}`);
          const pics = imgRes.data?.pictures || [];
          // Show all gallery-type images (uploaded via FacilityImagesManager)
          const gallery = pics.filter(p => p.image_type === 'gallery');
          // Backwards compatibility: if no image_type tagged, show non-default extras
          if (gallery.length === 0) {
            const nonDefault = pics.filter(p => !p.is_default && !p.image_type);
            setFacilityImages(nonDefault.length > 1 ? nonDefault.slice(1) : nonDefault);
          } else {
            setFacilityImages(gallery);
          }
        } catch (e) { /* no gallery images */ }
      }
    } catch (error) {
      console.error('Error fetching provider:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookAppointment = () => {
    // Navigate to unified booking flow with provider data
    navigate('/booking', { 
      state: { 
        provider: provider,
        providerType: 'provider'
      } 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Provider Not Found</h2>
          <button
            onClick={() => navigate('/healthcare-providers')}
            className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Back to Providers
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate('/healthcare-providers')}
          className="mb-4 flex items-center text-blue-600 hover:text-blue-700"
        >
          ← Back to Providers
        </button>
        
        <div className="bg-white rounded-lg shadow-lg p-5 sm:p-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            <div className="order-2 md:order-1">
              <div className="text-center mb-6 md:text-center">
                {provider.logo && (
                  <img
                    src={provider.logo}
                    alt={provider.name}
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    className="w-28 h-28 sm:w-32 sm:h-32 rounded-full mx-auto mb-4 object-cover bg-gray-100"
                  />
                )}
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{provider.name}</h1>
                <p className="text-blue-600 text-lg mt-2">{provider.category}</p>
                {provider.specializations && provider.specializations.length > 0 && (
                  <div className="mt-2">
                    {provider.specializations.map((spec, idx) => (
                      <span key={idx} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-1 mb-1">
                        {spec}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {provider.description && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-1">About</h3>
                    <p className="text-gray-600">{provider.description}</p>
                  </div>
                )}

                {provider.address && (
                  <div>
                    <div className="flex items-start">
                      <span className="text-gray-500 mr-2">📍</span>
                      <div className="flex-1">
                        <p className="text-gray-700">{provider.address}</p>
                        {provider.area && <p className="text-gray-500 text-sm">{provider.area}</p>}
                        
                        {/* Google Maps Link */}
                        {provider.google_maps_link ? (
                          <a
                            href={provider.google_maps_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            View on Google Maps
                          </a>
                        ) : (provider.latitude && provider.longitude) ? (
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${provider.latitude},${provider.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Get Directions
                          </a>
                        ) : provider.address && (
                          <a
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(provider.address + ', Kuwait')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            Search on Google Maps
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {provider.phone && (
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">📞</span>
                    <a href={`tel:${provider.phone}`} className="text-blue-600 hover:underline">
                      {provider.phone}
                    </a>
                  </div>
                )}

                {provider.email && (
                  <div className="flex items-center">
                    <span className="text-gray-500 mr-2">✉️</span>
                    <a href={`mailto:${provider.email}`} className="text-blue-600 hover:underline">
                      {provider.email}
                    </a>
                  </div>
                )}

                {provider.working_hours && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-1">Working Hours</h3>
                    <p className="text-gray-600">{provider.working_hours}</p>
                  </div>
                )}

                {provider.consultation_fee > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-green-800 font-semibold">
                      Starting from {provider.consultation_fee} KD
                    </p>
                  </div>
                )}

                {provider.services && provider.services.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Services Offered</h3>
                    <div className="flex flex-wrap gap-2">
                      {provider.services.map((service, idx) => (
                        <span key={idx} className="bg-gray-100 text-gray-700 text-sm px-3 py-1 rounded">
                          {typeof service === 'string' ? service : service.name || service.description || 'Service'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {provider.amenities && provider.amenities.length > 0 && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-2">Amenities</h3>
                    <div className="flex flex-wrap gap-2">
                      {provider.amenities.map((amenity, idx) => (
                        <span key={idx} className="bg-purple-100 text-purple-700 text-sm px-3 py-1 rounded">
                          {amenity}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Facility Images Gallery */}
            {facilityImages.length > 0 && (
              <div className="order-3 md:order-3 md:col-span-2 mt-2 md:mt-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Facility Gallery</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {facilityImages.map((img, idx) => (
                    <div key={idx} className="aspect-video rounded-lg overflow-hidden border border-gray-200 bg-gray-100">
                      <img
                        src={img.is_default ? img.file_url : `${API}${img.file_url}`}
                        alt={`Facility ${idx + 1}`}
                        loading="lazy"
                        onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                        className="w-full h-full object-cover md:hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="order-1 md:order-2">
              <div className="bg-blue-50 p-5 sm:p-6 rounded-lg md:sticky md:top-4">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Book an Appointment</h2>
                
                <button
                  onClick={handleBookAppointment}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Book Appointment
                </button>

                <p className="text-sm text-gray-600 text-center mt-4">
                  Click to select date, time, and service
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <ReviewSection providerId={id} providerType="provider" />
      </div>
    </div>
  );
};

const Appointments = () => {
  const { user: legacyUser } = useAuth();
  const { user: unifiedUser, userRole } = useUnifiedAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use unified auth if available, fallback to legacy
  const user = unifiedUser || legacyUser;
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState({
    upcoming: [],
    previous: [],
    cancelled: []
  });
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  // Receipt for the booking the user just completed (passed via router state)
  const justBookedAppointment = location?.state?.appointment || null;
  const justBookedMessage = location?.state?.message || null;
  
  // Determine user role
  const getUserRole = () => {
    if (userRole) return userRole.toLowerCase();
    if (unifiedUser?.user_type) return unifiedUser.user_type;
    if (legacyUser?.user_type) return legacyUser.user_type;
    return 'patient'; // default
  };
  
  const currentUserRole = getUserRole();

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    
    // Check for payment status on return from MyFatoorah
    checkPaymentReturn();
    
    // Only fetch appointments for patients
    if (currentUserRole === 'patient') {
      fetchAppointments();
    } else {
      setLoading(false);
    }
  }, [user, currentUserRole]);
  
  const checkPaymentReturn = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment_status');
    const appointmentId = urlParams.get('appointment_id');
    
    if (paymentStatus && appointmentId) {
      if (paymentStatus === 'success') {
        alert('✅ Payment successful! Your appointment is confirmed.');
      } else {
        alert('❌ Payment failed. Your appointment was not confirmed. Please try booking again.');
      }
      
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const fetchAppointments = async () => {
    try {
      const token = localStorage.getItem('sessionToken');
      const response = await axios.get(`${API}/appointments`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Split appointments into categories
      const now = new Date();
      const upcoming = [];
      const cancelled = [];
      const previous = [];
      
      response.data.forEach(apt => {
        const aptDate = new Date(`${apt.appointment_date}T${apt.appointment_time}`);
        
        if (apt.status === 'cancelled') {
          cancelled.push(apt);
        } else if (aptDate < now) {
          previous.push(apt);
        } else {
          upcoming.push(apt);
        }
      });
      
      // Sort each category by date (soonest first for upcoming, most recent first for past)
      upcoming.sort((a, b) => {
        const dateA = new Date(`${a.appointment_date}T${a.appointment_time}`);
        const dateB = new Date(`${b.appointment_date}T${b.appointment_time}`);
        return dateA - dateB;
      });
      
      previous.sort((a, b) => {
        const dateA = new Date(`${a.appointment_date}T${a.appointment_time}`);
        const dateB = new Date(`${b.appointment_date}T${b.appointment_time}`);
        return dateB - dateA;
      });
      
      cancelled.sort((a, b) => {
        const dateA = new Date(`${a.appointment_date}T${a.appointment_time}`);
        const dateB = new Date(`${b.appointment_date}T${b.appointment_time}`);
        return dateB - dateA;
      });
      
      setAppointments({
        upcoming,
        previous,
        cancelled
      });
    } catch (error) {
      console.error('Error fetching appointments:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const canCancelOrReschedule = (appointment) => {
    // Check if appointment is at least 24 hours away
    const appointmentDateTime = new Date(`${appointment.appointment_date}T${appointment.appointment_time}`);
    const now = new Date();
    const hoursUntil = (appointmentDateTime - now) / (1000 * 60 * 60);
    return hoursUntil >= 24;
  };

  const handleCancelClick = (appointment) => {
    setSelectedAppointment(appointment);
    setCancelModalOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!selectedAppointment) return;
    
    setCancelling(true);
    try {
      const token = localStorage.getItem('sessionToken');
      await axios.put(
        `${API}/appointments/${selectedAppointment.id}/cancel`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // Refresh appointments list
      await fetchAppointments();
      setCancelModalOpen(false);
      setSelectedAppointment(null);
      alert('Appointment cancelled successfully!');
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to cancel appointment. Please try again.';
      alert(errorMsg);
    } finally {
      setCancelling(false);
    }
  };

  const handleRescheduleClick = (appointment) => {
    // Navigate to the booking flow with provider/doctor info for rescheduling
    if (appointment.provider_type === 'doctor') {
      navigate(`/doctors/${appointment.doctor_id}`, {
        state: {
          rescheduleMode: true,
          appointmentId: appointment.id,
          currentDate: appointment.appointment_date,
          currentTime: appointment.appointment_time
        }
      });
    } else {
      // For providers, navigate to unified booking flow with provider data
      navigate('/booking', {
        state: {
          rescheduleMode: true,
          appointmentId: appointment.id,
          providerId: appointment.doctor_id,
          providerName: appointment.doctor_name,
          providerType: 'provider'
        }
      });
    }
  };

  const handleFollowUp = (appointment) => {
    // Navigate to booking with the same doctor/provider
    if (appointment.provider_type === 'doctor') {
      navigate(`/doctors/${appointment.doctor_id}`);
    } else {
      navigate(`/healthcare-providers/${appointment.doctor_id}`);
    }
  };

  const renderAppointmentCard = (appointment, showFollowUp = false) => (
    <div key={appointment.id} className="bg-white rounded-lg shadow p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h3 className="text-lg font-semibold text-gray-900">
              {appointment.doctor_name || 'Healthcare Provider'}
            </h3>
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              appointment.status === 'confirmed' ? 'bg-green-100 text-green-800' :
              appointment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
              appointment.status === 'cancelled' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {appointment.status}
            </span>
            {appointment.provider_type && (
              <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                {appointment.provider_type === 'doctor' ? '👨‍⚕️ Doctor' : '🏥 Provider'}
              </span>
            )}
          </div>
          <div className="text-gray-600 space-y-1">
            <div className="flex items-center gap-2">
              <span>📅</span>
              <span>{appointment.appointment_date} at {appointment.appointment_time}</span>
            </div>
            {appointment.reference_number && (
              <div className="flex items-center gap-2" data-testid={`appt-reference-${appointment.id}`}>
                <span>🎟️</span>
                <span className="font-mono text-sm font-semibold">
                  Reference:{' '}
                  <code className="bg-blue-50 border border-blue-200 text-blue-900 px-2 py-0.5 rounded">
                    {appointment.reference_number}
                  </code>
                </span>
              </div>
            )}
            {appointment.confirmation_number && !appointment.reference_number && (
              <div className="flex items-center gap-2">
                <span>🎫</span>
                <span className="font-mono text-sm">Confirmation: {appointment.confirmation_number}</span>
              </div>
            )}
            {appointment.patient_name && (
              <div className="flex items-center gap-2" data-testid={`appt-customer-${appointment.id}`}>
                <span>👤</span>
                <span>Customer: <span className="font-medium">{appointment.patient_name}</span></span>
              </div>
            )}
            {appointment.patient_phone && (
              <div className="flex items-center gap-2">
                <span>📞</span>
                <span>Phone: <span className="font-medium">{appointment.patient_phone}</span></span>
              </div>
            )}
            {appointment.patient_email && (
              <div className="flex items-center gap-2">
                <span>✉️</span>
                <span className="break-all">Email: <span className="font-medium">{appointment.patient_email}</span></span>
              </div>
            )}
            {appointment.service_name && (
              <div className="flex items-center gap-2">
                <span>⚕️</span>
                <span>Service: {typeof appointment.service_name === 'string' ? appointment.service_name : appointment.service_name.name || 'N/A'}</span>
              </div>
            )}
            {(appointment.addons || []).length > 0 && (
              <div className="flex items-center gap-2" data-testid="appointment-addons">
                <span>✨</span>
                <span>
                  Extras: {appointment.addons.map((a) => `${a.name} (+${Number(a.price).toFixed(3)} KD)`).join(', ')}
                </span>
              </div>
            )}
            {appointment.clinic_name && (
              <div className="flex items-center gap-2">
                <span>🏥</span>
                <span>{appointment.clinic_name}</span>
              </div>
            )}
            {appointment.symptoms && (
              <div className="flex items-center gap-2">
                <span>💬</span>
                <span>Symptoms: {appointment.symptoms}</span>
              </div>
            )}
            {appointment.consultation_fee > 0 && (
              <div className="flex items-center gap-2" data-testid="appointment-fee">
                <span>💰</span>
                <span>
                  Paid: {Number(appointment.amount_payable ?? appointment.consultation_fee).toFixed(3)} KD
                  {appointment.points_redeemed > 0 && (
                    <span className="text-emerald-700 font-medium">
                      {' '}(after {appointment.points_redeemed} points)
                    </span>
                  )}
                </span>
              </div>
            )}
            {appointment.points_earned > 0 && (
              <div className="flex items-center gap-2 text-emerald-700" data-testid="appointment-points-earned">
                <span>🎁</span>
                <span>Earned {appointment.points_earned} points</span>
              </div>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 sm:flex sm:flex-col gap-2 w-full sm:w-auto sm:shrink-0">
          {appointment.status === 'confirmed' && (
            <AddToCalendar appointment={appointment} compact={true} />
          )}
          <button
            type="button"
            onClick={() => generateBookingReceiptPdf(appointment, { mode: 'download' })}
            data-testid={`appt-receipt-btn-${appointment.id}`}
            className="bg-blue-50 text-blue-700 border border-blue-100 px-4 py-2.5 rounded hover:bg-blue-100 transition-colors text-sm whitespace-nowrap inline-flex items-center justify-center gap-1.5"
            title="Download branded EnayaHub receipt"
          >
            <span>📄</span> Receipt (PDF)
          </button>
          {appointment.status === 'confirmed' && !showFollowUp && (
            <button 
              className="bg-blue-100 text-blue-700 px-4 py-2.5 rounded hover:bg-blue-200 transition-colors text-sm whitespace-nowrap"
              onClick={() => {
                if (appointment.provider_type === 'doctor') {
                  navigate(`/doctors/${appointment.doctor_id}`);
                } else {
                  navigate(`/healthcare-providers/${appointment.doctor_id}`);
                }
              }}
            >
              View {appointment.provider_type === 'doctor' ? 'Doctor' : 'Provider'}
            </button>
          )}
          {appointment.status === 'confirmed' && canCancelOrReschedule(appointment) && (
            <>
              <button 
                className="bg-yellow-100 text-yellow-700 px-4 py-2.5 rounded hover:bg-yellow-200 transition-colors text-sm whitespace-nowrap"
                onClick={() => handleRescheduleClick(appointment)}
              >
                Reschedule
              </button>
              <button 
                className="bg-red-100 text-red-700 px-4 py-2.5 rounded hover:bg-red-200 transition-colors text-sm whitespace-nowrap"
                onClick={() => handleCancelClick(appointment)}
              >
                Cancel
              </button>
            </>
          )}
          {appointment.status === 'confirmed' && !canCancelOrReschedule(appointment) && (
            <p className="col-span-2 text-xs text-gray-500 text-center">
              Cannot cancel/reschedule<br/>(less than 24hrs)
            </p>
          )}
          {showFollowUp && (
            <button 
              className="bg-green-100 text-green-700 px-4 py-2.5 rounded hover:bg-green-200 transition-colors text-sm whitespace-nowrap"
              onClick={() => handleFollowUp(appointment)}
            >
              Book Follow-Up
            </button>
          )}
        </div>
      </div>
    </div>
  );

  if (!user) {
    return <Navigate to="/" />;
  }

  // Show doctor appointments management for doctors
  if (currentUserRole === 'doctor') {
    return <DoctorAppointmentsNew />;
  }

  // Show provider appointments management for providers  
  if (currentUserRole === 'provider') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Provider Dashboard</h1>
          
          <div className="bg-white rounded-lg shadow p-6">
            <div className="text-center py-12">
              <div className="text-blue-500 text-6xl mb-4">🏥</div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">Provider Appointments Management</h2>
              <p className="text-gray-600 mb-6">
                Manage your facility's appointments, staff schedules, and patient bookings.
              </p>
              <div className="space-y-3">
                <button 
                  onClick={() => navigate('/provider-dashboard')}
                  className="block w-full bg-blue-600 text-white py-3 px-6 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Go to Provider Dashboard
                </button>
                <button 
                  onClick={() => navigate('/simple-provider-dashboard')}
                  className="block w-full bg-gray-100 text-gray-700 py-3 px-6 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Simple Provider Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default patient appointments view
  const hasAnyAppointments = appointments.upcoming.length > 0 || appointments.previous.length > 0 || appointments.cancelled.length > 0;
  
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">My Appointments</h1>

        {/* Receipt for booking just completed */}
        {justBookedAppointment && (
          <div
            className="mb-8 rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white shadow-sm p-6"
            data-testid="booking-receipt-banner"
          >
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-2xl">✅</span>
                  <h2 className="text-xl font-bold text-emerald-900">
                    {justBookedMessage || 'Booking confirmed!'}
                  </h2>
                </div>
                <p className="text-sm text-emerald-800/80">
                  Save your reference number — quote it in any correspondence with EnayaHub support.
                </p>
              </div>
              {justBookedAppointment.reference_number && (
                <div className="text-right">
                  <div className="text-[11px] uppercase tracking-wider text-emerald-700/80 font-semibold">
                    Reference
                  </div>
                  <code
                    className="inline-block mt-1 bg-white border-2 border-emerald-300 text-emerald-900 px-3 py-1.5 rounded-lg font-mono text-base font-bold shadow-sm"
                    data-testid="booking-receipt-reference"
                  >
                    {justBookedAppointment.reference_number}
                  </code>
                </div>
              )}
            </div>

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 bg-white border border-emerald-100 rounded-lg p-4">
              {justBookedAppointment.patient_name && (
                <div data-testid="booking-receipt-name">
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Customer</div>
                  <div className="font-medium text-gray-900">{justBookedAppointment.patient_name}</div>
                </div>
              )}
              {justBookedAppointment.patient_phone && (
                <div data-testid="booking-receipt-phone">
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Phone</div>
                  <div className="font-medium text-gray-900">{justBookedAppointment.patient_phone}</div>
                </div>
              )}
              {justBookedAppointment.patient_email && (
                <div data-testid="booking-receipt-email">
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Email</div>
                  <div className="font-medium text-gray-900 break-all">{justBookedAppointment.patient_email}</div>
                </div>
              )}
              {(justBookedAppointment.appointment_date || justBookedAppointment.appointment_time) && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">When</div>
                  <div className="font-medium text-gray-900">
                    {justBookedAppointment.appointment_date}
                    {justBookedAppointment.appointment_time ? ` at ${justBookedAppointment.appointment_time}` : ''}
                  </div>
                </div>
              )}
              {justBookedAppointment.service_name && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Service</div>
                  <div className="font-medium text-gray-900">
                    {typeof justBookedAppointment.service_name === 'string'
                      ? justBookedAppointment.service_name
                      : (justBookedAppointment.service_name.name || 'N/A')}
                  </div>
                </div>
              )}
              {(justBookedAppointment.doctor_name || justBookedAppointment.clinic_name) && (
                <div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">Provider</div>
                  <div className="font-medium text-gray-900">
                    {justBookedAppointment.doctor_name || justBookedAppointment.clinic_name}
                  </div>
                </div>
              )}
            </div>

            <p className="mt-4 text-xs text-gray-500">
              A copy of this receipt has been sent to your WhatsApp and email (where available).
            </p>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => generateBookingReceiptPdf(justBookedAppointment, { mode: 'download' })}
                data-testid="booking-receipt-download-btn"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg shadow-sm transition-colors"
              >
                <span>⬇️</span> Download receipt (PDF)
              </button>
              <button
                type="button"
                onClick={() => generateBookingReceiptPdf(justBookedAppointment, { mode: 'print' })}
                data-testid="booking-receipt-print-btn"
                className="inline-flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium px-4 py-2 rounded-lg shadow-sm transition-colors"
              >
                <span>🖨️</span> Print receipt
              </button>
              <button
                type="button"
                data-testid="booking-receipt-share-btn"
                onClick={async () => {
                  const ref = justBookedAppointment.reference_number || '';
                  await shareContent({
                    title: `EnayaHub booking ${ref}`,
                    text: `My EnayaHub booking ${ref} on ${justBookedAppointment.appointment_date} at ${justBookedAppointment.appointment_time}.`,
                    url: ref ? `${window.location.origin}/booking/lookup/${ref}` : window.location.href,
                  });
                }}
                className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-4 py-2 rounded-lg shadow-sm transition-colors"
              >
                <span>📤</span> Share receipt
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading appointments...</p>
          </div>
        ) : !hasAnyAppointments ? (
          <div className="text-center py-12">
            <div className="text-gray-400 text-6xl mb-4">📅</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">No appointments found</h2>
            <p className="text-gray-600 mb-6">You haven't booked any appointments yet.</p>
            <button
              onClick={() => navigate('/healthcare-providers')}
              data-testid="appointments-find-provider-btn"
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('appointments.findProvider', 'Find a Provider')}
            </button>
          </div>
        ) : (
          <>
            {/* Upcoming Appointments */}
            {appointments.upcoming.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">📅 Upcoming Appointments</h2>
                <div className="space-y-4">
                  {appointments.upcoming.map((appointment) => renderAppointmentCard(appointment, false))}
                </div>
              </div>
            )}

            {/* Previous Appointments */}
            {appointments.previous.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-700 mb-4">📋 Previous Appointments</h2>
                <div className="space-y-4">
                  {appointments.previous.map((appointment) => renderAppointmentCard(appointment, true))}
                </div>
              </div>
            )}

            {/* Cancelled Appointments */}
            {appointments.cancelled.length > 0 && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-500 mb-4">❌ Cancelled Appointments</h2>
                <div className="space-y-4">
                  {appointments.cancelled.map((appointment) => renderAppointmentCard(appointment, false))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Cancel Confirmation Modal */}
        {cancelModalOpen && selectedAppointment && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Cancel Appointment</h3>
              <p className="text-gray-600 mb-2">
                Are you sure you want to cancel your appointment with:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <p className="font-semibold text-gray-900">{selectedAppointment.doctor_name}</p>
                <p className="text-sm text-gray-600">
                  {selectedAppointment.appointment_date} at {selectedAppointment.appointment_time}
                </p>
              </div>
              <p className="text-sm text-red-600 mb-6">
                ⚠️ This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setCancelModalOpen(false);
                    setSelectedAppointment(null);
                  }}
                  disabled={cancelling}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Keep Appointment
                </button>
                <button
                  onClick={handleCancelConfirm}
                  disabled={cancelling}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {cancelling ? 'Cancelling...' : 'Yes, Cancel'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main App Component
// Conditional Home Component
const ConditionalHome = () => {
  const { user: legacyUser } = useAuth();
  const { user: unifiedUser } = useUnifiedAuth();
  const user = unifiedUser || legacyUser;
  
  // Show authenticated dashboard for logged-in users
  if (user) {
    return <AuthenticatedHome />;
  }
  
  // Show the redesigned marketing landing page for visitors
  return <LandingPage />;
};

/**
 * Subscribes to Capacitor Universal Link / App Link events and routes them
 * through react-router so the same magic-link URLs work in the browser and in
 * the native app. No-op on the web (`onDeepLink` short-circuits).
 */
const DeepLinkBridge = () => {
  const navigate = useNavigate();
  useEffect(() => {
    const unsubscribe = onDeepLink((path) => {
      if (path) navigate(path);
    });
    return () => {
      try {
        unsubscribe && unsubscribe();
      } catch (_) { /* noop */ }
    };
  }, [navigate]);
  return null;
};

// Private staff schedule links open bare — no header, footer or chat bubble,
// so a staff member sees only their own day (and no support phone number).
const SiteChrome = ({ children }) => {
  const { pathname } = useLocation();
  if (pathname.startsWith('/my-schedule/')) return null;
  return children;
};

function App() {
  // SECURITY: Initialize token security system on app load
  useEffect(() => {
    initializeTokenSecurity();
  }, []);

  return (
    <ThemeProvider>
    <LanguageProvider>
      <AuthProvider>
        <UnifiedAuthProvider>
          <div className="App min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col transition-colors duration-200">
            <BrowserRouter>
              <DeepLinkBridge />
              <PageTracker />
              <SiteChrome><SiteHeader /></SiteChrome>
              <main className="flex-grow">
                <Routes>
                  <Route path="/" element={<ConditionalHome />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/healthcare-providers" element={<HealthcareProvidersPage />} />
                  <Route path="/healthcare-providers/:id" element={<ProviderDetail />} />
                  <Route path="/search" element={<SearchPage />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />
                  <Route path="/booking/manage/:token" element={<BookingManagePage />} />
                  <Route path="/doctors" element={<DoctorsPage />} />
                  <Route path="/doctor/profile-builder" element={<DoctorProfileBuilder />} />
                  <Route path="/doctors/:id" element={<DoctorDetail />} />
                  <Route path="/about" element={<AboutUs />} />
                  <Route
                    path="/terms"
                    element={
                      <div className="bg-white dark:bg-gray-950 py-16">
                        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 rounded-3xl border border-gray-200 dark:border-white/10 bg-white p-8" data-testid="terms-page">
                          <UserTerms />
                        </div>
                      </div>
                    }
                  />
                  <Route path="/join-provider-network" element={<ProviderPricingPage />} />
                  <Route path="/pricing" element={<ProviderPricingPage />} />
                  <Route path="/appointments" element={
                    <ProtectedRoute>
                      <Appointments />
                    </ProtectedRoute>
                  } />
                  <Route path="/medical-records" element={
                    <ProtectedRoute>
                      <MedicalRecords />
                    </ProtectedRoute>
                  } />
                  <Route path="/notifications" element={
                    <ProtectedRoute>
                      <Notifications />
                    </ProtectedRoute>
                  } />
                  <Route path="/messages" element={
                    <ProtectedRoute>
                      <Messages />
                    </ProtectedRoute>
                  } />
                  <Route path="/clinic-dashboard" element={
                    <ProtectedRoute>
                      <SimpleDoctorDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/doctor-test" element={<SimpleDoctorDashboard />} />
                  <Route path="/provider-dashboard" element={
                    <ProtectedRoute>
                      <ImprovedProviderDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/provider/schedule" element={
                    <ProtectedRoute>
                      <StaffTimelineBoard />
                    </ProtectedRoute>
                  } />
                  <Route path="/provider/staff-earnings" element={
                    <ProtectedRoute>
                      <StaffEarnings />
                    </ProtectedRoute>
                  } />
                  <Route path="/provider/walk-ins" element={
                    <ProtectedRoute>
                      <WalkInEarnings />
                    </ProtectedRoute>
                  } />
                  <Route path="/provider/schedule/list" element={
                    <ProtectedRoute>
                      <StaffDailySchedule />
                    </ProtectedRoute>
                  } />
                  <Route path="/provider/schedule/list/:staffId" element={
                    <ProtectedRoute>
                      <StaffDailySchedule />
                    </ProtectedRoute>
                  } />
                  <Route path="/provider/schedule/:staffId" element={
                    <ProtectedRoute>
                      <StaffDailySchedule />
                    </ProtectedRoute>
                  } />
                  <Route path="/my-schedule/:token" element={<StaffScheduleLinkView />} />
                  <Route path="/my-clients" element={
                    <ProtectedRoute>
                      <MyClients />
                    </ProtectedRoute>
                  } />
                  <Route path="/provider/clients/:clientId" element={
                    <ProtectedRoute>
                      <ClientProfile />
                    </ProtectedRoute>
                  } />
                  <Route path="/subscription" element={
                    <ProtectedRoute>
                      <SubscriptionManagement />
                    </ProtectedRoute>
                  } />
                  <Route path="/provider-test" element={<SuperSimpleProviderDashboard />} />
                  <Route path="/calendly-test" element={<SimpleCalendlyStyleDashboard />} />
                  <Route path="/sms-demo" element={<SMSDemo />} />
                  <Route path="/paci-demo" element={<PACIShowcase />} />
                  {/* Separate booking flows for doctors and providers */}
                  <Route path="/booking/provider" element={<ProviderBookingFlow />} />
                  <Route path="/booking/doctor" element={<DoctorBookingFlow />} />
                  
                  {/* Provider booking route with ID parameter */}
                  <Route path="/provider/:providerId/book" element={<ProviderBookingFlow />} />
                  
                  {/* Legacy unified booking route - redirect to provider */}
                  <Route path="/booking" element={<ProviderBookingFlow />} />
                  <Route path="/payment" element={<PaymentPage />} />
                  <Route path="/admin" element={<AdminPanel />} />
                  <Route path="/admin/subscriptions" element={<AdminSubscriptionsEnhanced />} />
                  <Route path="/enhanced-signup" element={<EnhancedSignupDemo />} />
                  <Route path="/register" element={<RegisterPage />} />
                  <Route path="/sign-up/provider" element={<ProviderSignupPage />} />
                  <Route path="/sign-up/doctor" element={<DoctorSignupPage />} />
                  <Route path="/legacy-booking" element={
                    <ProtectedRoute>
                      <BookingSystem />
                    </ProtectedRoute>
                  } />
                  <Route path="/consultations" element={
                    <ProtectedRoute>
                      <ConsultationHub />
                    </ProtectedRoute>
                  } />
                  <Route path="/revenue-dashboard" element={
                    <ProtectedRoute adminOnly={true}>
                      <RevenueDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/patient-profile" element={
                    <ProtectedRoute>
                      <PatientProfile />
                    </ProtectedRoute>
                  } />
                  <Route path="/wallet" element={
                    <ProtectedRoute>
                      <WalletDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/influencer" element={
                    <ProtectedRoute>
                      <InfluencerDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/guest-booking" element={<GuestBooking />} />
                </Routes>
              </main>
              <SiteChrome>
                <Footer />
                <InstallPWA />
                <AIAssistant />
              </SiteChrome>
            </BrowserRouter>
          </div>
        </UnifiedAuthProvider>
      </AuthProvider>
    </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;